/**
 * Inventory (07) — the Magasinier surface FeatureModule. Five screens, all under /inventory/*:
 *
 *   /inventory/catalogue   — reactif catalog CRUD + lot reception + FEFO/expiry/risk drawer + sweep + recall launch
 *   /inventory/reorder     — reorder dashboard (OK/BAS/RUPTURE) with KPI tiles + a Recharts distribution
 *   /inventory/recettes    — the RecetteReactif (consumption recipe) editor
 *   /inventory/charges     — the ReactifCharge active-lot loader (one active lot per automate+reagent)
 *   /inventory/recall      — the lot → Resultat recall worklist (preview → confirm)
 *   /inventory/cycles      — the cycle-count cockpit (open → count → review → post, INV-8)
 *
 * Gating model. The five screens need DIFFERENT capabilities (read vs recette.manage vs charge.set vs
 * recall.run), and the router applies ONE module-level gate per route. So — like the finance module —
 * we keep the MODULE gate empty (any authenticated user reaches the shell) and gate two ways:
 *   1. each NAV item carries its own requiredCapabilities (the side-nav shows only what you may open);
 *   2. each SCREEN renders its own ForbiddenState when its read/entry capability is missing, and every
 *      mutating action is gated by its exact server capability via useCan.
 * The server re-checks every request — useCan only hides/disables UI (never the trust boundary).
 *
 * Capabilities used (exact server keys, role MAGASINIER):
 *   inventory.reactif.read|create|update · inventory.lot.receive|read · inventory.mouvement.create
 *   inventory.recette.manage · inventory.charge.set · inventory.recall.run · inventory.inventaire.run
 */
import type { FeatureModule } from "@/app";
import { INV_CAPS } from "./caps";
import { registerInventoryMessages } from "./messages";
import { CataloguePage } from "./screens/CataloguePage";
import { ReorderPage } from "./screens/ReorderPage";
import { RecettesPage } from "./screens/RecettesPage";
import { ChargesPage } from "./screens/ChargesPage";
import { RecallPage } from "./screens/RecallPage";
import { CyclesPage } from "./screens/CyclesPage";

const inventoryModule: FeatureModule = {
  id: "inventory",
  title: "Stock / Magasin",
  // Empty module gate (see header): per-screen ForbiddenState + per-nav-item caps do the gating.
  requiredCapabilities: [],
  routes: [
    { path: "/inventory/catalogue", element: <CataloguePage /> },
    { path: "/inventory/reorder", element: <ReorderPage /> },
    { path: "/inventory/recettes", element: <RecettesPage /> },
    { path: "/inventory/charges", element: <ChargesPage /> },
    { path: "/inventory/recall", element: <RecallPage /> },
    { path: "/inventory/cycles", element: <CyclesPage /> },
  ],
  nav: [
    {
      id: "inventory-catalogue",
      path: "/inventory/catalogue",
      label: "nav.inventory.catalogue",
      icon: "⬡",
      order: 70,
      section: "nav.section.stock",
      requiredCapabilities: [INV_CAPS.reactifRead],
    },
    {
      id: "inventory-reorder",
      path: "/inventory/reorder",
      label: "nav.inventory.reorder",
      icon: "▤",
      order: 71,
      section: "nav.section.stock",
      requiredCapabilities: [INV_CAPS.reactifRead],
    },
    {
      id: "inventory-recettes",
      path: "/inventory/recettes",
      label: "nav.inventory.recettes",
      icon: "≣",
      order: 72,
      section: "nav.section.stock",
      requiredCapabilities: [INV_CAPS.recetteManage],
    },
    {
      id: "inventory-charges",
      path: "/inventory/charges",
      label: "nav.inventory.charges",
      icon: "▸",
      order: 73,
      section: "nav.section.stock",
      requiredCapabilities: [INV_CAPS.chargeSet],
    },
    {
      id: "inventory-recall",
      path: "/inventory/recall",
      label: "nav.inventory.recall",
      icon: "⟲",
      order: 74,
      section: "nav.section.stock",
      requiredCapabilities: [INV_CAPS.recallRun],
    },
    {
      id: "inventory-cycles",
      path: "/inventory/cycles",
      label: "nav.inventory.cycles",
      icon: "▦",
      order: 75,
      section: "nav.section.stock",
      requiredCapabilities: [INV_CAPS.inventaireRun],
    },
  ],
  onRegister() {
    registerInventoryMessages();
  },
};

export default inventoryModule;
